// 声音：合成音效配方（RECIPES，数据表，可测试）+ 背景音乐播放器（Music）。走母系统游戏音频管线（陆衍统筹）：
// ../../../docs/游戏音频管线.md。音效全部 Web Audio 现场合成，不用外部文件（许可干净、零字节）；
// 背景音乐由作者按 public/bgm/BGM需求说明.md 生成，经 tools/audio/bgm_pipeline.py 处理后放 public/bgm/处理后BGM/，
// 缺文件时安静，不报错。音色取中国传统打击与弹拨：木鱼、鼓、锣、编钟、五声音阶。设计表见 docs/音效表.md。

export type SfxEvent =
  | 'select' | 'deselect' | 'play' | 'pass' | 'bomb' | 'straightFlush' | 'jokerBomb' | 'borrowWind'
  | 'first' | 'report' | 'tribute' | 'myTurn' | 'error' | 'roundWin' | 'roundLose' | 'passA' | 'button';

/** 一个声部：tone = 振荡器；noise = 带通噪声（可扫频）。时间单位秒。 */
export interface Voice {
  kind: 'tone' | 'noise';
  freq: number;
  dur: number;
  vol: number;
  wave?: OscillatorType;
  slide?: number; // 结束频率 = freq × slide
  delay?: number;
  q?: number;
}

const PENTA = [523.25, 587.33, 659.25, 783.99, 880, 1046.5]; // C 宫五声：宫商角徵羽 + 高宫
const bell = (f: number, delay = 0, vol = 0.16, dur = 1.1): Voice[] => [
  { kind: 'tone', freq: f, dur, vol, wave: 'sine', delay },
  { kind: 'tone', freq: f * 2.76, dur: dur * 0.5, vol: vol * 0.35, wave: 'sine', delay }, // 编钟的非整数泛音
];

export const RECIPES: Record<SfxEvent, Voice[]> = {
  select: [{ kind: 'tone', freq: 1760, dur: 0.035, vol: 0.12, wave: 'triangle' }],
  deselect: [{ kind: 'tone', freq: 1320, dur: 0.035, vol: 0.1, wave: 'triangle' }],
  play: [ // “掼”：牌拍在桌上的闷响 + 纸声
    { kind: 'tone', freq: 150, dur: 0.12, vol: 0.45, wave: 'sine', slide: 0.6 },
    { kind: 'noise', freq: 1100, dur: 0.08, vol: 0.35, q: 0.9 },
  ],
  pass: [ // 木鱼两下
    { kind: 'tone', freq: 640, dur: 0.07, vol: 0.18, wave: 'sine', slide: 0.9 },
    { kind: 'tone', freq: 640, dur: 0.07, vol: 0.14, wave: 'sine', slide: 0.9, delay: 0.11 },
  ],
  bomb: [ // 大鼓 + 墨散开的噪声
    { kind: 'tone', freq: 72, dur: 0.55, vol: 0.9, wave: 'sine', slide: 0.5 },
    { kind: 'tone', freq: 118, dur: 0.3, vol: 0.4, wave: 'triangle', slide: 0.6, delay: 0.02 },
    { kind: 'noise', freq: 320, dur: 0.38, vol: 0.55, q: 0.7 },
  ],
  straightFlush: [...bell(880), ...bell(1318.5, 0.06), ...bell(1760, 0.12)], // 编钟三响
  jokerBomb: [ // 锣
    { kind: 'tone', freq: 98, dur: 2.2, vol: 0.7, wave: 'sine' },
    { kind: 'tone', freq: 147, dur: 1.9, vol: 0.3, wave: 'triangle', slide: 0.97 },
    { kind: 'tone', freq: 233, dur: 1.4, vol: 0.18, wave: 'sine', delay: 0.04 },
    { kind: 'noise', freq: 650, dur: 1.2, vol: 0.28, q: 2 },
  ],
  borrowWind: [{ kind: 'noise', freq: 500, dur: 0.75, vol: 0.32, q: 1.2, slide: 4 }], // 风声扫过
  first: PENTA.slice(0, 5).map((f, i) => ({ kind: 'tone', freq: f, dur: 0.22, vol: 0.16, wave: 'triangle', delay: i * 0.07 })),
  report: [{ kind: 'tone', freq: 980, dur: 0.06, vol: 0.16, wave: 'sine', slide: 0.92 }], // 高木鱼一下
  tribute: [{ kind: 'noise', freq: 2600, dur: 0.2, vol: 0.22, q: 0.6, slide: 0.6 }], // 牌滑过桌面
  myTurn: bell(1046.5, 0, 0.1, 0.5),
  error: [{ kind: 'tone', freq: 165, dur: 0.16, vol: 0.12, wave: 'square', slide: 0.85 }],
  roundWin: [659.25, 783.99, 1046.5].map((f, i) => ({ kind: 'tone', freq: f, dur: 0.3, vol: 0.17, wave: 'triangle', delay: i * 0.1 })),
  roundLose: [659.25, 587.33, 440].map((f, i) => ({ kind: 'tone', freq: f, dur: 0.32, vol: 0.14, wave: 'sine', delay: i * 0.12 })),
  passA: [ // 金榜题名：锣一声接五声音阶上行
    { kind: 'tone', freq: 98, dur: 1.8, vol: 0.6, wave: 'sine' },
    { kind: 'noise', freq: 650, dur: 1, vol: 0.22, q: 2 },
    ...[...PENTA, 1174.7, 1318.5].map((f, i) => ({ kind: 'tone' as const, freq: f, dur: 0.3, vol: 0.15, wave: 'triangle' as const, delay: 0.25 + i * 0.08 })),
  ],
  button: [{ kind: 'tone', freq: 1200, dur: 0.025, vol: 0.08, wave: 'triangle' }],
};

/** 高频事件限流（毫秒）：同类在这段时间内只响一次。 */
export const THROTTLE: Partial<Record<SfxEvent, number>> = { select: 40, deselect: 40, button: 60, play: 60, pass: 80 };
/** 这些事件响时把背景音乐压低到 30% 约 1.2 秒（管线标准值）。 */
export const DUCK: SfxEvent[] = ['bomb', 'jokerBomb', 'passA'];

export class Sound {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private sfx!: GainNode;
  private noise!: AudioBuffer;
  private last: Partial<Record<SfxEvent, number>> = {};
  muted = false;
  music = new Music();

  /** 必须在用户手势里调用（iOS 要求）；重复调用无害。 */
  unlock(): void {
    if (!this.ctx) {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : 0.8;
      this.master.connect(this.ctx.createDynamicsCompressor()).connect(this.ctx.destination); // 音效总线压缩
      this.sfx = this.ctx.createGain();
      this.sfx.gain.value = 0.6;
      this.sfx.connect(this.master);
      this.noise = this.ctx.createBuffer(1, this.ctx.sampleRate / 2, this.ctx.sampleRate); // 0.5 秒循环播放（性能优化 11-03：原 1 秒且不循环，1.2 秒的噪声音效会被截断）
      const d = this.noise.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
    if (this.ctx.state !== 'running') void this.ctx.resume();
    this.music.start(this.muted);
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.ctx) this.master.gain.setTargetAtTime(m ? 0 : 0.8, this.ctx.currentTime, 0.05);
    this.music.setMuted(m);
  }

  play(ev: SfxEvent): void {
    const c = this.ctx;
    if (!c || this.muted) return;
    const gap = THROTTLE[ev];
    const now = performance.now();
    if (gap && (this.last[ev] ?? -1e9) + gap > now) return;
    this.last[ev] = now;
    if (DUCK.includes(ev)) this.music.duck();
    for (const v of RECIPES[ev]) {
      const t = c.currentTime + (v.delay ?? 0);
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(v.vol, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, t + v.dur);
      g.connect(this.sfx);
      if (v.kind === 'tone') {
        const o = c.createOscillator();
        o.type = v.wave ?? 'sine';
        o.frequency.setValueAtTime(v.freq, t);
        if (v.slide) o.frequency.exponentialRampToValueAtTime(Math.max(30, v.freq * v.slide), t + v.dur);
        o.connect(g);
        o.start(t);
        o.stop(t + v.dur + 0.02);
      } else {
        const s = c.createBufferSource();
        s.buffer = this.noise;
        s.loop = true; // 白噪声循环听不出接缝
        const f = c.createBiquadFilter();
        f.type = 'bandpass';
        f.Q.value = v.q ?? 0.8;
        f.frequency.setValueAtTime(v.freq, t);
        if (v.slide) f.frequency.exponentialRampToValueAtTime(v.freq * v.slide, t + v.dur);
        s.connect(f).connect(g);
        s.start(t);
        s.stop(t + v.dur + 0.02);
      }
    }
  }
}

/**
 * 背景音乐：读 bgm/处理后BGM/manifest.json（键 = 程序名，见 BGM需求说明），按状态选曲、1.2 秒交叉淡化、压低。
 * 没有处理后的 BGM 时安静（作者还没生成时不报错）。音乐总音量 0.32，不走音效压缩总线。
 */
export class Music {
  private tracks: Record<string, string> = {};
  private el: HTMLAudioElement | null = null;
  private current = '';
  private want = 'table';
  private muted = false;
  private loaded = false;
  private readonly volume = 0.32;

  async start(muted: boolean): Promise<void> {
    this.muted = muted;
    if (!this.loaded) {
      this.loaded = true;
      try {
        const res = await fetch('bgm/处理后BGM/manifest.json');
        if (res.ok) {
          const man = (await res.json()) as Record<string, { file: string }>;
          for (const [k, v] of Object.entries(man)) this.tracks[k] = `bgm/处理后BGM/${v.file}`;
        }
      } catch { /* 没有 BGM：安静 */ }
    }
    this.switchTo(this.want);
  }

  /** 状态 → 曲目：table 牌桌循环、result 一副结算、champion 过 A。 */
  switchTo(name: string): void {
    this.want = name;
    const src = this.tracks[name];
    if (!src || this.current === name) return;
    const old = this.el;
    const el = new Audio(src);
    el.loop = name === 'table';
    el.volume = 0;
    this.el = el;
    this.current = name;
    if (!this.muted) void el.play().catch(() => undefined);
    this.fade(el, this.muted ? 0 : this.volume, 1200);
    if (old) { this.fade(old, 0, 1200); setTimeout(() => old.pause(), 1250); }
  }

  duck(): void {
    if (!this.el || this.muted) return;
    this.el.volume = this.volume * 0.3;
    setTimeout(() => this.el && !this.muted && this.fade(this.el, this.volume, 400), 1200);
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (!this.el) return;
    if (m) this.el.pause();
    else { void this.el.play().catch(() => undefined); this.fade(this.el, this.volume, 400); }
  }

  private fade(el: HTMLAudioElement, to: number, ms: number): void {
    const from = el.volume, t0 = performance.now();
    const tick = () => {
      const k = Math.min(1, (performance.now() - t0) / ms);
      el.volume = from + (to - from) * k;
      if (k < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}
